import type { ChartWidgetConfig } from "../../types/chart"

/**
 * Filters split keys to only return visible series
 * @param allSplitKeys - All available split keys from chartData
 * @param config - Widget configuration containing hiddenSeries
 * @returns Array of visible split keys in original order
 */
export function getVisibleSplitKeys(
  allSplitKeys: readonly string[],
  config: ChartWidgetConfig,
): string[] {
  const hiddenSeries = config.hiddenSeries
  if (!hiddenSeries || hiddenSeries.length === 0) {
    return [...allSplitKeys]
  }
  return allSplitKeys.filter((key) => !hiddenSeries.includes(key))
}

/**
 * Gets the original color index for a series key
 * Maintains color consistency when series are hidden/shown
 * @param seriesKey - Split key to find index for
 * @param allSplitKeys - All split keys (including hidden)
 * @returns Original index in full split keys array
 */
export function getOriginalSeriesIndex(
  seriesKey: string,
  allSplitKeys: readonly string[],
): number {
  return allSplitKeys.indexOf(seriesKey)
}

/**
 * Hidden series after a legend label click, following Datadog: the clicked
 * series becomes the only visible one, and clicking the lone visible series
 * shows everything again. Hidden keys absent from `allSplitKeys` are kept so
 * they stay hidden if they appear in later data.
 */
export function isolateSeries(
  hiddenSeries: readonly string[] | undefined,
  key: string,
  allSplitKeys: readonly string[],
): string[] {
  const absent = (hiddenSeries ?? []).filter(
    (entry) => !allSplitKeys.includes(entry),
  )
  const visible = allSplitKeys.filter((entry) => !hiddenSeries?.includes(entry))
  if (visible.length === 1 && visible[0] === key) {
    return absent
  }
  return [...absent, ...allSplitKeys.filter((entry) => entry !== key)]
}

/** Hidden series after a legend checkbox click. */
export function toggleHiddenSeries(
  hiddenSeries: readonly string[] | undefined,
  key: string,
): string[] {
  const hidden = hiddenSeries ?? []
  return hidden.includes(key)
    ? hidden.filter((entry) => entry !== key)
    : [...hidden, key]
}
